import type { ApiError } from '../../apiErrors';

/**
 * BAR-1564: what the sign-in page says when Keycloak's sign-out could not
 * start, or is still on its way after the sign-out view's 10 s. BARK's own
 * session is gone either way; Keycloak's may not be, so the next sign-in asks
 * for credentials.
 */
export const SIGNED_OUT_OF_BARK_NOTICE = 'You are signed out of BARK.';

/**
 * The words the sign-in page shows for a refused sign-in: auth-service's
 * `/sso/token` (Firebase SSO) or `/session/provision` (Keycloak, BAR-1564).
 * Both refuse with the same detail shapes. Mapped by HTTP status so a change in
 * the backend's internal wording cannot silently break the mapping. `sso`
 * keeps the Firebase SSO wording; the Keycloak path covers password sign-ins
 * too, so it says "Sign-in".
 */
export function signInRefusalMessage(error: unknown, { sso }: { sso: boolean }): string {
    // A refused free signup says why in words written for the person
    // ("Your organisation already uses BARK..."): show those as they are.
    const signupMessage = (error as ApiError)?.userMessage;
    if (signupMessage) return signupMessage;
    const raw = error instanceof Error ? error.message : '';
    const httpStatus = (error as ApiError)?.status;
    if (httpStatus === 403) {
        if (raw.toLowerCase().includes('disabled')) {
            return 'Your account has been disabled. Please contact support.';
        }
        if (raw.toLowerCase().includes('verified')) {
            return 'Your email address has not been verified by your identity provider.';
        }
        return sso ? 'SSO is not available for your organization.' : 'Sign-in is not available for your organization.';
    }
    if (httpStatus === 503) {
        return sso
            ? 'SSO service is temporarily unavailable. Please try again later.'
            : 'Sign-in is temporarily unavailable. Please try again later.';
    }
    return sso ? 'SSO login failed. Please try again.' : 'Sign-in failed. Please try again.';
}
